import {
  CODING_AGENTS_CANCEL_SETUP_CHANNEL,
  CODING_AGENTS_GET_STATE_CHANNEL,
  CODING_AGENTS_INSTALL_CHANNEL,
  CODING_AGENTS_INSTALL_STEP_CHANNEL,
  CODING_AGENTS_LAUNCH_CHANNEL,
  CODING_AGENTS_LIST_SESSIONS_CHANNEL,
  CODING_AGENTS_OPEN_SESSION_CHANNEL,
  CODING_AGENTS_REOPEN_SIGN_IN_CHANNEL,
  CODING_AGENTS_SEND_MESSAGE_CHANNEL,
  CODING_AGENTS_SESSION_CHANGED_CHANNEL,
  CODING_AGENTS_SET_CONNECTED_CHANNEL,
  CODING_AGENTS_SIGN_IN_CHANNEL,
  CODING_AGENTS_STOP_CHANNEL,
  type AgentActionResult,
  type AgentLaunchRequest,
  type AgentMessage,
  type AgentSession,
  type AgentSessionRef,
  type AgentSetupResult,
  type CodingAgentId,
  type InstallStepEvent,
  type OpenSessionRequest,
  type CodingAgentState,
} from '@electron/shared/coding-agents.types';

import { ElectronAPI } from '../base/ElectronAPI';
import { type ElectronUnsubscribe } from '../base/types';

export type {
  AgentActionResult,
  AgentLaunchRequest,
  AgentMessage,
  AgentSession,
  AgentSessionRef,
  AgentSetupResult,
  CodingAgentId,
  CodingAgentState,
  InstallStep,
  SetupActivity,
} from '@electron/shared/coding-agents.types';

export class CodingAgentsAPI extends ElectronAPI {
  getState(): Promise<CodingAgentState[]> {
    return this.invoke<CodingAgentState[]>(CODING_AGENTS_GET_STATE_CHANNEL);
  }

  setConnected(
    id: CodingAgentId,
    connected: boolean
  ): Promise<CodingAgentState> {
    return this.invoke<CodingAgentState>(CODING_AGENTS_SET_CONNECTED_CHANNEL, {
      id,
      connected,
    });
  }

  /** Answers once the installer ends, which can take several minutes */
  install(id: CodingAgentId): Promise<AgentSetupResult> {
    return this.invoke(CODING_AGENTS_INSTALL_CHANNEL, { id });
  }

  /** Answers once the sign-in ends, which can take minutes while the user is in the browser */
  signIn(id: CodingAgentId): Promise<AgentSetupResult> {
    return this.invoke(CODING_AGENTS_SIGN_IN_CHANNEL, { id });
  }

  onInstallStep(
    callback: (event: InstallStepEvent) => void
  ): ElectronUnsubscribe {
    return this.addListener<InstallStepEvent>({
      eventType: CODING_AGENTS_INSTALL_STEP_CHANNEL,
      callback,
    });
  }

  /** Ends the install or sign-in under way for the agent */
  cancelSetup(id: CodingAgentId): Promise<void> {
    return this.invoke(CODING_AGENTS_CANCEL_SETUP_CHANNEL, { id });
  }

  reopenSignIn(id: CodingAgentId): Promise<AgentActionResult> {
    return this.invoke(CODING_AGENTS_REOPEN_SIGN_IN_CHANNEL, { id });
  }

  launch(
    request: AgentLaunchRequest
  ): Promise<AgentActionResult<{ sessionId: string }>> {
    return this.invoke(CODING_AGENTS_LAUNCH_CHANNEL, request);
  }

  sendMessage(message: AgentMessage): Promise<AgentActionResult> {
    return this.invoke(CODING_AGENTS_SEND_MESSAGE_CHANNEL, message);
  }

  stop(session: AgentSessionRef): Promise<AgentActionResult> {
    return this.invoke(CODING_AGENTS_STOP_CHANNEL, session);
  }

  openSession(session: OpenSessionRequest): Promise<AgentActionResult> {
    return this.invoke(CODING_AGENTS_OPEN_SESSION_CHANNEL, session);
  }

  listSessions(): Promise<AgentSession[]> {
    return this.invoke<AgentSession[]>(CODING_AGENTS_LIST_SESSIONS_CHANNEL);
  }

  onSessionChanged(
    callback: (session: AgentSession) => void
  ): ElectronUnsubscribe {
    return this.addListener<AgentSession>({
      eventType: CODING_AGENTS_SESSION_CHANGED_CHANNEL,
      callback,
    });
  }
}
